/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import CopyComponent from 'src/components/CopyComponent'
import { TextInput } from 'src/components/ts-components/TextInput'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'

interface TeamInfo {
  uid: string
  name?: string | null
  email?: string | null
  phone_number?: string | null
  website?: string | null
  notes?: string | null
}

interface TeamInfoSectionProps {
  teamInfo?: TeamInfo
  registrationId: string
  disableFields?: boolean
  updateFormData?: (field: string, value: string) => void
  teamName: string
  setTeamName: (name: string) => void
  disableTeamName?: boolean
  isComplimentary?: boolean
}

/**
 * 🔄 TeamInfoSection: Displays team information header with team details and ID copying functionality
 *
 * This component shows the team info section with copy functionality for Team ID
 * and Registration ID, plus team information form fields.
 */
const TeamInfoSection = ({
  teamInfo,
  registrationId,
  disableFields = false,
  teamName,
  setTeamName,
  disableTeamName = false,
  isComplimentary = false,
}: TeamInfoSectionProps) => {
  console.log('🔄 TeamInfoSection: Rendering team info section', {
    teamInfo,
    registrationId,
    disableFields,
  })

  return (
    <div
      css={css`
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        align-self: stretch;
        gap: 24px;
      `}
    >
      {/* Left Column - Team Information */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
          flex: 1;
        `}
      >
        <Text
          variant='captionLarge'
          color={colors.WHITE}
          weight='semiBold'
          style={`
            align-self: flex-start;
          `}
        >
          Team Information
        </Text>
        {isComplimentary && (
          <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
            This registration is complimentary
          </Text>
        )}

        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            align-self: stretch;
            margin-bottom: 16px;
          `}
        >
          <img
            width={56}
            height={56}
            css={css`
              border-radius: 8px;
              border: 2px solid ${colors.SECONDARY_300};
              background: ${colors.NEUTRAL_0};
              object-fit: contain;
              max-width: 100%;
              max-height: 100%;
              width: 56px;
              height: 56px;
            `}
            src={
              getImageThumbnailId(teamInfo) ||
              '/images/generic_placeholders/placeholder_team.png'
            }
            alt='Team placeholder'
          />
        </div>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            width: 100%;
          `}
        >
          <TextInput
            name='teamName'
            value={teamName}
            setValue={(e) => setTeamName(e)}
            fieldErrors={[]}
            customError={{}}
            label='Team Name'
            disabled={disableFields || disableTeamName}
            placeholder='Enter team name'
            style={css`
              width: 64.1%;
            `}
          />
        </div>
      </div>

      {/* Right Column - Copy Components */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
        `}
      >
        {teamInfo?.uid && (
          <CopyComponent
            customText='Team ID'
            id={teamInfo.uid}
            alternativeBackground={disableFields}
          />
        )}
        {registrationId && (
          <CopyComponent
            customText='Registration ID'
            id={registrationId}
            alternativeBackground={disableFields}
          />
        )}
      </div>
    </div>
  )
}

export default TeamInfoSection
